/**
 * Anchor resolution — how a tour step finds the element it points at.
 *
 * This file is the reason the rest of the app carries no tour code. A step
 * describes its target declaratively; the strategies below turn that
 * description into a DOM element at the moment the tour runs.
 *
 * 🔴 STRATEGY ORDER IS PRECEDENCE, AND IT IS DELIBERATE.
 * An explicit `data-tour-node` attribute always wins, then an author-supplied
 * CSS selector, then structural matching (a role/landmark plus the element's own
 * accessible label). The precise strategies are tried FIRST so that a page which
 * later opts in gets targeted precisely with no schema edit — the reverse order
 * would make adoption a silent no-op.
 *
 * Structural matching is last because it is the most fragile: it reads
 * user-visible copy, so re-wording a nav label stops that step resolving. That
 * is an accepted trade for zero coupling — a step that cannot resolve is
 * SKIPPED, never an error, so the worst case is a shorter tour rather than a
 * broken one.
 */

export interface AnchorSpec {
  /**
   * Explicit opt-in hook. Resolves to `[data-tour-node="<node>"]`.
   * Nothing in the app is required to provide it; when it exists it wins.
   */
  node?: string;
  /** Raw CSS selector. Escape hatch for chrome with no sensible attribute. */
  selector?: string;
  /**
   * Structural match: find an element inside `within` whose accessible label is
   * one of `labels`. Used for the sidebar, whose items are identified by the
   * framework's own `data-sidebar="menu-button"` attribute rather than anything
   * this module asked for.
   */
  structural?: {
    /** Container selector — scopes the search so a label can't match elsewhere. */
    within: string;
    /** Candidate selector inside the container. */
    match: string;
    /**
     * Accepted labels, in preference order, compared case-insensitively after
     * trimming. A LIST rather than one string because this is user-visible copy:
     * "AI" was renamed to "AI Agents" in the sidebar, which silently dropped
     * that step. Aliases let a rename land without breaking the tour, and let
     * one tour serve installs whose wording differs.
     */
    labels?: string[];
    /**
     * Selector for the element that CARRIES the label, searched inside each
     * candidate. Use when the element to highlight is a composite whose own
     * text is a run-together of everything inside it — the sidebar's Nexa AI
     * card reads "Nexa AIBetaOpen the full-page AI workspace", which matches no
     * sensible label, while the `<span>` inside it reads exactly "Nexa AI".
     * The CANDIDATE is still what gets highlighted, not the label element.
     */
    labelWithin?: string;
    /**
     * Label-free fallback: the candidate matches if it CONTAINS an element
     * matching this selector. Tried only after every label has failed.
     *
     * 🔴 This is what makes a step survive a COLLAPSED sidebar. Collapsed, the
     * Nexa AI card drops its `<span>`s entirely and renders icon-only — so
     * label matching found nothing and the step silently disappeared from the
     * tour, which is exactly how a user reports "it is not in the guide". The
     * icon survives both states.
     */
    containsSelector?: string;
  };
  /**
   * A submodule row inside a module's accordion.
   *
   * 🔴 SCOPED TO ITS OWN MODULE'S ROW, never matched by label across the whole
   * sidebar. Submodule labels are NOT unique: "Overview" exists under both
   * Appointments and Commerce *and* as a top-level module, and "Automations" is
   * both a module and one of its own submodules. A global label match would
   * point at whichever happened to come first in the document.
   *
   * Scoping is the module row's own `menu-item` container, which holds the
   * trigger and the rows it reveals — see `accordionScopeFor` for why the
   * trigger's `aria-controls` cannot be used for this.
   */
  submenu?: {
    /** Accepted labels for the parent module row (current name first). */
    moduleLabels: string[];
    /** Accepted labels for the submodule row itself. */
    labels: string[];
  };
  /**
   * The smallest element that contains every one of these OTHER anchors —
   * for ringing a group of related controls (e.g. two radio buttons in the
   * same choice) as one unit.
   *
   * 🔴 EXISTS SO A TASK NEVER NEEDS THE HOST APP TO ADD A WRAPPING HOOK IT HAS
   * NO OTHER REASON TO HAVE. Two sibling controls with no shared test id or
   * stable class still have a real DOM ancestor — this resolves each
   * sub-anchor independently, then walks up to their nearest common one,
   * rather than asking the app to grow a `data-testid` around a group that
   * exists purely for this module's benefit.
   *
   * Every sub-anchor must resolve for the group to resolve at all — a group
   * around a control that is not there would highlight a container that does
   * not actually hold what the step is describing.
   */
  group?: AnchorSpec[];
}

/** How an anchor was found. Surfaced for diagnostics, never for behaviour. */
export type AnchorStrategy = 'node' | 'selector' | 'structural' | 'submenu' | 'group';

/**
 * The nearest ancestor shared by every element, or null when they are not all
 * in the same document (or the list is empty).
 *
 * Walks up from the first element's own ancestor chain (a Set for O(1)
 * membership) and returns the first ancestor every OTHER element also has in
 * its chain — which, by construction, is the lowest one they all share.
 */
function nearestCommonAncestor(elements: HTMLElement[]): HTMLElement | null {
  if (elements.length === 0) return null;
  if (elements.length === 1) return elements[0];

  const chainOf = (el: Element): Element[] => {
    const chain: Element[] = [];
    let node: Element | null = el;
    while (node) {
      chain.push(node);
      node = node.parentElement;
    }
    return chain;
  };

  const [first, ...rest] = elements;
  let candidates = new Set(chainOf(first));

  for (const el of rest) {
    const chain = new Set(chainOf(el));
    for (const node of candidates) {
      if (!chain.has(node)) candidates.delete(node);
    }
  }
  if (candidates.size === 0) return null;

  // `candidates` is unordered; the lowest (most specific) shared ancestor is
  // whichever appears FIRST in the first element's own chain, since that
  // chain is ordered from the element itself upward.
  for (const node of chainOf(first)) {
    if (candidates.has(node)) return node as HTMLElement;
  }
  return null;
}

export interface ResolvedAnchor {
  element: HTMLElement;
  strategy: AnchorStrategy;
}

/** `querySelectorAll` that cannot throw on an author's bad selector. */
function safeQueryAll(selector: string, root: ParentNode = document): Element[] {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/**
 * Poll until `probe` returns something, or give up.
 *
 * Frame-driven rather than a fixed timeout: the first attempt runs immediately,
 * so a condition that already holds costs nothing at all, and a mount that lands
 * in 14ms is picked up on the next frame instead of after an arbitrary wait.
 *
 * Lives here, next to the resolver, because every caller is waiting on the DOM
 * to catch up with something this module just asked the host to do — opening a
 * group, opening the nav drawer — and both engines needed the same loop.
 */
export function waitForDom<T>(probe: () => T | null, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = probe();
      if (hit !== null) return resolve(hit);
      if (Date.now() - startedAt >= timeoutMs) return resolve(null);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * The label a human would read for this element.
 *
 * Checks `aria-label`, then an `<img alt>` (the sidebar labels its icons that
 * way and keeps the text label `sr-only` when collapsed), then text content.
 * Taking the first non-empty one means a collapsed, icon-only sidebar still
 * resolves — text content alone would be empty there.
 */
export function accessibleLabel(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) return aria.trim();

  const img = el.querySelector('img[alt]');
  const alt = img?.getAttribute('alt');
  if (alt && alt.trim()) return alt.trim();

  return (el.textContent || '').trim();
}

/** Is this element actually on screen? */
export function isVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

/**
 * Is this element sealed off behind a modal layer?
 *
 * 🔴 `isVisible` CANNOT ANSWER THIS, AND THAT GAP IS A REAL, REPRODUCED BUG.
 * It is a pure CSS `display`/`visibility`/box test, so a control sitting BEHIND
 * an open dialog passes it: the control is still laid out, still painted, just
 * covered. `TaskEngine.enter` already carries a note about the same trap on the
 * forward path ("for a button that opens a dialog on top of itself, `isVisible()`
 * still finds it, so the ring drew a circle floating over the dialog's own
 * backdrop") — this is the test that half of the engine was missing.
 *
 * Radix's `DismissableLayer`/`hideOthers` stamps `aria-hidden="true"` on every
 * subtree OUTSIDE an open modal, all the way up to `<body>`, which is exactly the
 * question being asked here: not "is it painted" but "does the browser consider
 * it part of the screen the user is currently on". Measured in a real browser
 * with the campaign composer open: the page's own `New Campaign` button reports
 * `aria-hidden` ancestor DIV, `elementFromPoint` at its centre returns the
 * dialog, and `getComputedStyle` still says `visible`.
 *
 * `inert` is tested too — it is the platform's own version of the same thing, and
 * the dialog primitives are gradually moving to it.
 *
 * 🔴 DELIBERATELY NOT A HIT TEST. `document.elementFromPoint` would also answer
 * "covered", but it answers "scrolled out of its container" the same way — and a
 * target that merely needs scrolling to is one the flow scrolls to and rings
 * perfectly well. Using it here would refuse every off-screen step in a long
 * form.
 */
export function isOccluded(el: Element): boolean {
  let node: Element | null = el;
  while (node && node !== document.body) {
    if (node.getAttribute('aria-hidden') === 'true') return true;
    if (node.hasAttribute('inert')) return true;
    node = node.parentElement;
  }
  return false;
}

/** Slack allowed at an edge before an element counts as gone from view. */
const VIEW_TOLERANCE = 4;

/**
 * Has this element been scrolled out of the box it lives in?
 *
 * 🔴 THIS IS THE OTHER HALF OF "IS IT WORTH POINTING AT", AND ITS ABSENCE IS A
 * REPORTED BUG. `isVisible` is a pure CSS test and `isOccluded` only asks about
 * modal layers, so an element pushed out of its own scroller passes both — and
 * the ring is then painted at coordinates the user cannot see. Measured in the
 * form builder: the "Add block" button the create-form flow rings sits at
 * `y: 469` on a fresh form, and every field the flow adds pushes it down. By the
 * third it is at `y: 798`, against a drawer scrollport that ends at `726`. The
 * step was live, the anchor resolved, and the highlight was drawn over the
 * drawer's footer, around nothing.
 *
 * Deliberately answers "GONE", not "partly cut off": a tall row hanging a few
 * pixels past an edge is still the thing the user is looking at, and treating
 * that as out of view would have the engine scrolling on almost every step.
 *
 * 🔴 SCROLLABLE ANCESTORS ONLY (plus the window). An `overflow: hidden` box
 * clips too, but no amount of scrolling fixes one — the same reasoning
 * `room.ts`'s `visibleBand` gives for the bands it chooses to measure.
 */
export function isOutOfView(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  if (rect.bottom <= VIEW_TOLERANCE || rect.top >= viewportHeight - VIEW_TOLERANCE) return true;
  if (rect.right <= VIEW_TOLERANCE || rect.left >= viewportWidth - VIEW_TOLERANCE) return true;

  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (!/auto|scroll|overlay/.test(`${style.overflowY} ${style.overflowX}`)) continue;
    const box = node.getBoundingClientRect();
    // A collapsed or unmeasured container clips everything; skipping it keeps
    // the answer honest rather than permanently "gone".
    if (box.width === 0 && box.height === 0) continue;
    if (rect.bottom <= box.top + VIEW_TOLERANCE || rect.top >= box.bottom - VIEW_TOLERANCE) return true;
    if (rect.right <= box.left + VIEW_TOLERANCE || rect.left >= box.right - VIEW_TOLERANCE) return true;
  }

  return false;
}

/**
 * Can the user actually see and reach this element right now?
 *
 * The test to use wherever the question is "is this a thing worth POINTING AT",
 * as opposed to `isVisible`'s "is this laid out", which is the right test for
 * choosing between two rendered copies of the same control (see `resolveAnchor`).
 */
export function isReachable(el: Element): boolean {
  return isVisible(el) && !isOccluded(el);
}

/**
 * Resolve an anchor, or `null` when nothing matches.
 *
 * Returning null is a first-class outcome: sidebar entries are gated by plan,
 * permissions and an explicit hidden-modules list, so a tour written against the
 * full product must degrade to whatever THIS user can see.
 */
export function resolveAnchor(spec: AnchorSpec): ResolvedAnchor | null {
  // 🔴 EVERY MATCH IS CONSIDERED, NOT JUST THE FIRST.
  // This shell renders some chrome twice — a desktop control and a mobile
  // equivalent — with only one of them on screen. Testing `querySelector`'s
  // single result meant a step resolved to nothing whenever the hidden copy
  // happened to come first in the document.
  const firstVisible = (selector: string): HTMLElement | null =>
    (safeQueryAll(selector).find(isVisible) as HTMLElement | undefined) ?? null;

  if (spec.node) {
    const el = firstVisible(`[data-tour-node="${CSS.escape(spec.node)}"]`);
    if (el) return { element: el, strategy: 'node' };
  }

  if (spec.selector) {
    const el = firstVisible(spec.selector);
    if (el) return { element: el, strategy: 'selector' };
  }

  if (spec.submenu) {
    const { moduleLabels, labels } = spec.submenu;
    const scope = accordionScopeFor(moduleLabels);
    if (scope) {
      for (const want of labels.map((l) => l.trim().toLowerCase()).filter(Boolean)) {
        for (const item of safeQueryAll(HOST.sidebarSubItem, scope)) {
          if (isVisible(item) && accessibleLabel(item).toLowerCase() === want) {
            return { element: item as HTMLElement, strategy: 'submenu' };
          }
        }
      }
    }
  }

  if (spec.structural) {
    const { within, match, labels = [], labelWithin, containsSelector } = spec.structural;
    const wanted = labels.map((l) => l.trim().toLowerCase()).filter(Boolean);

    /** Does this candidate carry one of the wanted labels? */
    const labelMatches = (candidate: Element, want: string): boolean => {
      if (!labelWithin) return accessibleLabel(candidate).toLowerCase() === want;
      // The label lives on a descendant; the candidate is still the target.
      return safeQueryAll(labelWithin, candidate).some(
        (inner) => (inner.textContent || '').trim().toLowerCase() === want,
      );
    };

    // Every container, not just the first: the sidebar renders a desktop rail
    // AND a mobile sheet, and only one of them is on screen at a time.
    const candidates = () =>
      safeQueryAll(within).flatMap((container) => safeQueryAll(match, container));

    // Pass 1 — labels, in preference order across ALL containers before moving
    // to the next label, so an exact current name always beats a legacy alias
    // whichever container happens to hold it.
    for (const want of wanted) {
      for (const candidate of candidates()) {
        if (isVisible(candidate) && labelMatches(candidate, want)) {
          return { element: candidate as HTMLElement, strategy: 'structural' };
        }
      }
    }

    // Pass 2 — the label-free fallback. Runs only when no label matched, so a
    // labelled match is always preferred: it is the more meaningful signal, and
    // this pass is deliberately looser.
    if (containsSelector) {
      for (const candidate of candidates()) {
        if (isVisible(candidate) && safeQueryAll(containsSelector, candidate).length > 0) {
          return { element: candidate as HTMLElement, strategy: 'structural' };
        }
      }
    }
  }

  if (spec.group && spec.group.length > 0) {
    const elements: HTMLElement[] = [];
    for (const sub of spec.group) {
      const hit = resolveAnchor(sub)?.element;
      // Every sub-anchor must resolve — see the doc comment on `group`.
      if (!hit) break;
      elements.push(hit);
    }
    if (elements.length === spec.group.length) {
      const container = nearestCommonAncestor(elements);
      if (container && isVisible(container)) {
        return { element: container, strategy: 'group' };
      }
    }
  }

  return null;
}

/* ------------------------------------------------------------------ *
 * Known host-app hooks.
 *
 * The ONLY place in this module that knows anything about the surrounding
 * application's markup. Everything else composes these, so re-pointing the
 * module at a different shell is an edit to this block alone.
 * ------------------------------------------------------------------ */

export const HOST = {
  /**
   * shadcn's own sidebar attributes — NOT something this module asked the app
   * to add. That is what keeps `AppSidebar.tsx` free of tour code.
   */
  sidebar: '[data-sidebar="sidebar"]',
  sidebarItem: '[data-sidebar="menu-button"]',
  /** The row container: holds a module's trigger AND the submodule rows it reveals. */
  sidebarMenuItem: '[data-sidebar="menu-item"]',
  /**
   * Module rows that are ACCORDION TRIGGERS — i.e. the ones with submodules.
   * `data-state` is present on exactly those (measured: 10 of the 15 rows), which
   * is how the tour tells a collapsible group from a plain link without knowing
   * anything about the menu data.
   *
   * 🔴 NOT `aria-expanded`, WHICH DRIVER.JS OVERWRITES.
   * On every highlight, driver.js stamps `aria-expanded="true"`,
   * `aria-controls="driver-popover-content"` and `aria-haspopup="dialog"` onto the
   * element it is pointing at, and REMOVES all three when it moves on. Measured
   * consequences, all of which this module hit at once:
   *   - a plain row became "expandable" for the duration of its own step (11 rows
   *     matched `[aria-expanded]` instead of 10),
   *   - `isAccordionOpen` read driver's fake `"true"` for the module being
   *     described, so the group was never actually opened,
   *   - and `aria-controls` pointed at driver's popover, so a submodule lookup
   *     scoped by it searched the popover and found nothing.
   * `data-state` is Radix's own and driver.js never touches it, so every one of
   * those reads is taken from it instead.
   */
  sidebarAccordion: '[data-sidebar="menu-button"][data-state]',
  /** Submodule rows inside an expanded accordion. */
  sidebarSubItem: '[data-sidebar="menu-sub-button"]',
  /**
   * Composite cards in the sidebar that are not menu items — currently just the
   * Nexa AI workspace card, which is a `div[role="button"]` rather than a
   * `SidebarMenuButton`.
   */
  sidebarCard: '[role="button"]',
  /**
   * The Nexa AI card's icon. Survives the collapsed sidebar, where the card's
   * text labels are not rendered at all.
   */
  nexaCardIcon: 'svg.lucide-bot',
  /**
   * The header's Nexa AI launcher. A `title` attribute is user-facing copy, so
   * this is a weaker hook than a data attribute — but it is the only stable
   * thing on that button, and a miss means the step is skipped rather than
   * misdirected.
   */
  headerAiLauncher: '[title="NEXA AI (Beta)"]',
  /**
   * The controls that change the SHAPE of the navigation, in the order they are
   * tried. See `ensureSidebarUsable` for why the tour touches these at all.
   *
   * Each is a list because the same job is done by a different control at each
   * breakpoint, and only one of them is ever on screen: below `sm` the nav is a
   * drawer opened from the top bar, above it a rail that collapses to icons with
   * the expand control in the top bar and the collapse control in its own header.
   * shadcn's own `[data-sidebar="trigger"]` is listed last in each — this app
   * does not render it, but a host that does gets picked up for free.
   */
  sidebarOpen: ['[data-tour="sidebar-trigger"]', '[aria-label="Open navigation menu"]', '[data-sidebar="trigger"]'],
  sidebarClose: ['[aria-label="Close navigation menu"]', '[data-sidebar="trigger"]'],
  sidebarExpand: ['[aria-label="Expand sidebar"]', '[data-sidebar="trigger"]'],
  sidebarCollapse: ['[aria-label="Collapse sidebar"]', '[data-sidebar="trigger"]'],
  /**
   * Where the Quick Tour button is portaled. An explicit opt-in slot, falling
   * back to a floating launcher when absent (see `HeaderSlot`), so the module
   * degrades to visible-but-relocated rather than invisible.
   */
  headerSlot: '[data-tour-slot="header-actions"]',
  /**
   * Host chrome hidden for the DURATION of a tour.
   *
   * 🔴 Always-on-top overlays PAINT OVER A HIGHLIGHT. The sandbox badge is fixed
   * to the bottom-left with a z-index above driver.js's stage, so it covered the
   * lower half of the Nexa AI card exactly while that step was explaining it —
   * the highlight was correctly placed and simply could not be seen.
   *
   * Suppression is reversed on teardown, so nothing is permanently hidden, and
   * entries matching nothing are silently ignored — which is what lets this
   * module drop into a host that has none of them.
   */
  suppressDuringTour: ['#wenext-sandbox-badge'],
} as const;

/**
 * Build the anchor spec for a sidebar entry identified by its visible label.
 * Pass several labels to tolerate a rename (current name first).
 */
export function sidebarAnchor(label: string | string[], node?: string): AnchorSpec {
  return {
    node,
    structural: {
      within: HOST.sidebar,
      match: HOST.sidebarItem,
      labels: Array.isArray(label) ? label : [label],
    },
  };
}

/* ------------------------------------------------------------------ *
 * Accordion control — opening the group a submodule step lives in.
 * ------------------------------------------------------------------ */

/**
 * The accordion trigger row for a module, by label.
 *
 * 🔴 RETURNS NULL WHILE THE RAIL IS ICON-COLLAPSED, even though a row with the
 * right label and a `data-state` is sitting right there. Collapsed, this shell
 * renders a module with submodules as a HOVER CARD, not a collapsible — and
 * Radix stamps `data-state` on a hover-card trigger too, so `sidebarAccordion`
 * matches it and everything downstream believes the group can be opened. What
 * actually happened: `openAccordion` clicked a hover trigger (which does nothing
 * on click), the engine then waited out its full mount budget for rows that can
 * never appear — once per module, visibly — and the module's popover promised
 * "Includes: …" for submodules the user was never shown.
 *
 * The whole run avoids that state by expanding the rail before it starts (see
 * `ensureSidebarUsable`); this is the guard for the case where it could not.
 */
export function accordionTriggerFor(moduleLabels: string[]): HTMLElement | null {
  if (isSidebarIconCollapsed()) return null;
  const wanted = moduleLabels.map((l) => l.trim().toLowerCase()).filter(Boolean);
  for (const want of wanted) {
    for (const container of safeQueryAll(HOST.sidebar)) {
      for (const trigger of safeQueryAll(HOST.sidebarAccordion, container)) {
        if (isVisible(trigger) && accessibleLabel(trigger).toLowerCase() === want) {
          return trigger as HTMLElement;
        }
      }
    }
  }
  return null;
}

/**
 * The region that holds a module's submodule rows — its own `menu-item`
 * container, which wraps both the trigger and the panel the trigger reveals.
 *
 * 🔴 DELIBERATELY NOT the trigger's `aria-controls`, even though Radix wires that
 * to the exact panel. The one module whose rows a sub-flow needs to find is the
 * one currently being HIGHLIGHTED, and driver.js has by then replaced its
 * `aria-controls` with `driver-popover-content` — so the lookup searched driver's
 * own popover for sidebar rows and reported the module as having none. See
 * `HOST.sidebarAccordion`.
 *
 * Returns the container whether the group is open or closed; a closed group
 * simply has no rows inside it, and callers filter on visibility anyway.
 */
export function accordionScopeFor(moduleLabels: string[]): HTMLElement | null {
  const trigger = accordionTriggerFor(moduleLabels);
  if (!trigger) return null;
  return (trigger.closest(HOST.sidebarMenuItem) as HTMLElement | null) ?? trigger.parentElement;
}

/**
 * Is this module's group open?
 *
 * Reads Radix's `data-state`, NOT `aria-expanded` — driver.js forces the latter
 * to `"true"` on the row it is highlighting, which is exactly the row asked about
 * here. Falls back to the ground truth (are there rows on screen?) for a host
 * whose trigger carries no `data-state`, so the answer degrades to something
 * observable rather than to a confident wrong one.
 */
export function isAccordionOpen(trigger: HTMLElement): boolean {
  const state = trigger.getAttribute('data-state');
  if (state) return state === 'open';
  const scope = trigger.closest(HOST.sidebarMenuItem) ?? trigger.parentElement;
  return scope ? safeQueryAll(HOST.sidebarSubItem, scope).some(isVisible) : false;
}

/**
 * Open a module's group so its submodule rows exist in the DOM.
 *
 * 🔴 DRIVEN BY A REAL CLICK on the host's own trigger, not by setting
 * `aria-expanded` or unhiding the panel. The open/closed state belongs to the
 * host's accordion component; forcing the attribute would leave the component
 * believing it is still closed, so its next render would collapse the group again
 * — mid-step, with the tour pointing at a row that no longer exists. Clicking
 * also inherits whatever else the host does on open, including closing whichever
 * group was open before, which is why this module never has to tidy up after
 * itself.
 *
 * Returns the trigger when one exists (already-open counts as success), null when
 * this module has no group on screen — the caller uses that to decide whether a
 * sub-flow is offered at all.
 *
 * `.click()` works during a run even though driver.js sets
 * `pointer-events: none` on the page: that only blocks real pointer input, and a
 * dispatched click is unaffected.
 */
export function openAccordion(moduleLabels: string[]): HTMLElement | null {
  const trigger = accordionTriggerFor(moduleLabels);
  if (!trigger) return null;
  if (!isAccordionOpen(trigger)) trigger.click();
  return trigger;
}

/** Every accordion trigger currently open. Used to tidy up when leaving a group. */
export function openAccordions(): HTMLElement[] {
  return safeQueryAll(HOST.sidebar)
    .flatMap((c) => safeQueryAll(HOST.sidebarAccordion, c))
    .filter((el) => isVisible(el) && isAccordionOpen(el as HTMLElement)) as HTMLElement[];
}

/**
 * Close every group except the one this step is about, and wait for them to go.
 *
 * 🔴 THE HOST DOES NOT ALWAYS DO THIS, and where it does not is exactly where a
 * short screen falls apart. This shell keeps ONE open group at a time — but only
 * as a side effect of opening another one: `setOpenAccordion(id)` runs when a
 * trigger is clicked, and nothing clicks a trigger when the tour walks from a
 * module WITH submodules to a module WITHOUT any. The first module's six rows
 * stay open behind the second one, which on a short viewport is most of the
 * screen spent on a module the tour has already finished describing — and it is
 * what pushes the module being described out of the list, out from under the ring,
 * or off the screen entirely.
 *
 * So the tour asks for it explicitly on every move: only the current stage's
 * module stays open.
 *
 * 🔴 CLICKED, NOT FORCED, for the same reason as `openAccordion` — the state
 * belongs to the host's component, and an attribute written behind its back is
 * undone by its next render. Awaited, because the collapse changes the position
 * of every row below it and the caller is about to measure them.
 *
 * Returns whether anything was closed, so a caller can tell a settled layout from
 * one that is about to move.
 */
export async function collapseAccordionsExcept(keepLabels: string[]): Promise<boolean> {
  // An icon rail has no accordions at all — see `accordionTriggerFor`.
  if (isSidebarIconCollapsed()) return false;

  const keep = keepLabels.map((label) => label.trim().toLowerCase()).filter(Boolean);
  const stale = openAccordions().filter(
    (trigger) => !keep.includes(accessibleLabel(trigger).trim().toLowerCase()),
  );
  if (stale.length === 0) return false;

  stale.forEach((trigger) => trigger.click());
  await waitForDom(
    () => (stale.every((trigger) => !isAccordionOpen(trigger)) ? true : null),
    SIDEBAR_SHAPE_TIMEOUT_MS,
  );
  return true;
}

/* ------------------------------------------------------------------ *
 * Making the navigation USABLE before a run — the adaptive half.
 *
 * 🔴 THE SIDEBAR IS A DIFFERENT COMPONENT AT DIFFERENT WIDTHS, and a tour that
 * walks modules is a tour of the sidebar. Three shapes exist in this shell:
 *
 *   - EXPANDED RAIL — labels and collapsible groups. Everything resolves.
 *   - ICON RAIL (collapsed) — the same rows, icon-only, and modules with
 *     submodules become HOVER CARDS rather than collapsibles. Module steps still
 *     resolve (their `img alt` survives), but no group can ever be opened, so
 *     every sub-flow silently came to nothing.
 *   - DRAWER (below the shell's mobile breakpoint) — a modal sheet whose content
 *     is UNMOUNTED while closed. `[data-sidebar="sidebar"]` does not exist, so
 *     every module step is skipped and the run degrades to the header alone.
 *
 * So the run asks the host to put the navigation into the one shape a menu
 * walkthrough can actually be given in, and puts it back afterwards. Driven by
 * the host's OWN controls, never by writing state or classes: the open/collapsed
 * state belongs to the sidebar component, and forcing the DOM would leave it
 * believing otherwise and collapse the rail again on its next render — mid-step.
 * ------------------------------------------------------------------ */

/** The visible sidebar container, or null when the drawer is closed. */
function sidebarShell(): HTMLElement | null {
  return (safeQueryAll(HOST.sidebar).find(isVisible) as HTMLElement | undefined) ?? null;
}

/**
 * The desktop wrapper that carries the rail's expanded/collapsed state.
 *
 * Matched on `[data-state][data-side]` together: `data-state` alone is on a
 * great many things (every Radix trigger the tour walks past), while the pair
 * is unique to the sidebar's own root.
 */
function sidebarStateHost(): HTMLElement | null {
  const shell = sidebarShell();
  return (shell?.closest('[data-state][data-side]') as HTMLElement | null) ?? null;
}

/** Is the visible sidebar the mobile drawer rather than the desktop rail? */
function isSidebarDrawer(): boolean {
  return sidebarShell()?.getAttribute('data-mobile') === 'true';
}

/** Is the desktop rail collapsed to icons? See `accordionTriggerFor`. */
export function isSidebarIconCollapsed(): boolean {
  return sidebarStateHost()?.getAttribute('data-state') === 'collapsed';
}

/**
 * Which of the navigation's shapes is on screen right now.
 *
 * 🔴 THE SIGNAL A LIVE RUN WATCHES FOR, and the reason it is a NAME rather than
 * a width. The three shapes are three different component trees (see the block
 * comment above), so crossing between them REPLACES every sidebar row with a
 * new node — the elements a run resolved before the change are detached or
 * `display: none`, and a detached node measures 0×0 at 0,0, which draws the
 * highlight as a small box in the top-left corner of the page. That is the
 * "the ring is on one module and the popover is describing another" report: the
 * ring is not on a module at all, it is in the corner the first module happens
 * to sit in.
 *
 * Comparing the NAME rather than `window.innerWidth` means the check fires once
 * per real shape change instead of on every pixel of a window drag, and it also
 * catches a shape change the user made themselves mid-run (collapsing the rail),
 * which no width comparison would see.
 */
export type SidebarShape = 'absent' | 'drawer' | 'rail' | 'rail-collapsed';

export function sidebarShape(): SidebarShape {
  if (!sidebarShell()) return 'absent';
  if (isSidebarDrawer()) return 'drawer';
  return isSidebarIconCollapsed() ? 'rail-collapsed' : 'rail';
}

/**
 * Is this element clipped out of sight by the viewport or by a scroll container
 * between it and the document?
 *
 * 🔴 `isVisible` DOES NOT ANSWER THIS, and the difference is the whole of the
 * height half of the bug. A sidebar row scrolled out of the sidebar's own
 * overflow container still has a real box and still passes every CSS check —
 * it is simply somewhere the user cannot see, and the highlight is drawn there,
 * over whatever chrome sits at that edge. Shortening the viewport is exactly
 * what pushes a row out of that container, so the row a tour is standing on
 * moves out from under the stage without anything about the element changing.
 */
export function isAnchorClipped(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  if (rect.top < 0 || rect.left < 0 || rect.bottom > viewportHeight || rect.right > viewportWidth) {
    return true;
  }

  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    // `hidden` counts: a clipped box is just as invisible as a scrolled-away one,
    // and the sidebar's own list is `overflow-hidden` while it animates.
    if (!/auto|scroll|hidden|overlay/.test(`${style.overflowY} ${style.overflowX}`)) continue;
    const box = parent.getBoundingClientRect();
    // A container with no box of its own (an unmeasured ancestor) would clip
    // everything; skip it rather than report a false positive.
    if (box.width === 0 && box.height === 0) continue;
    if (rect.top < box.top || rect.bottom > box.bottom) return true;
    if (rect.left < box.left || rect.right > box.right) return true;
  }

  return false;
}

/**
 * Bring a clipped anchor back into view, and say whether it had to.
 *
 * `'nearest'` on both axes so this scrolls the LEAST it can: the target is a row
 * in a list the user is reading, and centring it would re-scroll the sidebar
 * under them on every resize. Guarded by `isAnchorClipped` for the same reason —
 * an unconditional call fights the user's own scrolling, and would re-enter
 * through the scroll listener that watches for it.
 */
export function keepAnchorInView(el: HTMLElement): boolean {
  if (!isAnchorClipped(el)) return false;
  try {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  } catch {
    // Older engines reject the options object; position is a nicety here.
    el.scrollIntoView?.();
  }
  return true;
}

/** First visible match for any of these selectors, in order. */
function firstVisibleOf(selectors: readonly string[]): HTMLElement | null {
  for (const selector of selectors) {
    const hit = safeQueryAll(selector).find(isVisible) as HTMLElement | undefined;
    if (hit) return hit;
  }
  return null;
}

/**
 * What `ensureSidebarUsable` changed, so teardown can put it back.
 *
 * 🔴 RESTORED RATHER THAN LEFT AS-IS, unlike an accordion group the run opens.
 * A group left open is exactly the state a user reaches by clicking that row
 * themselves. A NAVIGATION DRAWER left open covers the screen the tour just
 * finished describing, and a rail silently expanded takes width from every page
 * the user visits next — neither is a state they asked for.
 */
export interface SidebarRestore {
  /** The drawer was closed and this run opened it. */
  openedDrawer: boolean;
  /** The rail was icon-collapsed and this run expanded it. */
  expandedRail: boolean;
}

/** How long to wait for the host to re-render after a shape change. */
const SIDEBAR_SHAPE_TIMEOUT_MS = 900;

/**
 * Put the navigation into a shape a module walkthrough can be given in.
 *
 * Returns what it changed, or null when it changed nothing — which is both the
 * common case (an expanded rail) and the honest answer when there is no control
 * to drive, so teardown has nothing to undo.
 *
 * Never throws and never blocks a run: a host with no toggle simply keeps
 * whatever shape it is in, and the tour degrades to the steps that do resolve —
 * the same outcome as before this existed.
 */
export async function ensureSidebarUsable(): Promise<SidebarRestore | null> {
  const changed: SidebarRestore = { openedDrawer: false, expandedRail: false };

  // The drawer first: with the sheet closed there is no sidebar in the document
  // at all, so the collapsed-rail question cannot even be asked yet.
  if (!sidebarShell()) {
    firstVisibleOf(HOST.sidebarOpen)?.click();
    const appeared = await waitForDom(() => sidebarShell(), SIDEBAR_SHAPE_TIMEOUT_MS);
    if (!appeared) return null;
    changed.openedDrawer = true;
  }

  // The drawer always renders full-width with labels and real collapsibles, so
  // only the desktop rail can be in the icon state.
  if (!isSidebarDrawer() && isSidebarIconCollapsed()) {
    firstVisibleOf(HOST.sidebarExpand)?.click();
    const expanded = await waitForDom(
      () => (isSidebarIconCollapsed() ? null : true),
      SIDEBAR_SHAPE_TIMEOUT_MS,
    );
    if (expanded) changed.expandedRail = true;
  }

  return changed.openedDrawer || changed.expandedRail ? changed : null;
}

/**
 * Does this step point INTO the navigation?
 *
 * 🔴 ASKED OF THE SPEC, NOT OF THE DOM, and that is the whole point. On a
 * phone the navigation is a drawer that covers the page, so the question
 * "should the drawer be open for this step" has to be answerable while the
 * drawer is shut and the row the step wants does not exist yet — a resolve
 * cannot answer it, because a closed drawer resolves nothing.
 *
 * A structural match scoped to the sidebar, or a submodule row, is in the
 * navigation by construction. A raw selector is not, unless what it matches
 * happens to live there right now — which is exactly the right test for it,
 * since a selector says nothing about where its target is.
 */
export function anchorTargetsSidebar(anchor: AnchorSpec): boolean {
  if (anchor.submenu) return true;
  if (anchor.structural?.within === HOST.sidebar) return true;
  if (anchor.group?.some(anchorTargetsSidebar)) return true;

  const selectors = [anchor.node ? `[data-tour-node="${anchor.node}"]` : null, anchor.selector];
  return selectors.some((selector) =>
    selector ? safeQueryAll(selector).some((el) => el.closest(HOST.sidebar)) : false,
  );
}

/**
 * Close the navigation drawer, and wait for it to actually go.
 *
 * 🔴 THE DRAWER IS THE ONE SHAPE THAT COVERS THE PAGE. A rail sits beside the
 * content, so a step pointing at the header or at `<main>` is perfectly visible
 * next to it — but the drawer is a modal sheet over everything, and the tour
 * opens it at start (see `ensureSidebarUsable`). A step whose target is not in
 * the navigation was therefore being highlighted BEHIND the drawer: the stage cut
 * a hole over a control the user could not see, with the guide describing it.
 *
 * Returns whether the drawer is now gone — false when there was no control to
 * drive, in which case the caller has a covered target and nothing it can do
 * about it, which is still better than not trying.
 */
export async function closeSidebarDrawer(): Promise<boolean> {
  if (!isSidebarDrawer()) return true;
  firstVisibleOf(HOST.sidebarClose)?.click();
  return (await waitForDom(() => (sidebarShell() ? null : true), SIDEBAR_SHAPE_TIMEOUT_MS)) === true;
}

/** Undo `ensureSidebarUsable`. Safe to call with null, or twice. */
export function restoreSidebarShape(restore: SidebarRestore | null): void {
  if (!restore) return;
  // Collapse before closing: the collapse control lives INSIDE the sidebar, so
  // closing the drawer first would take it out of the document.
  if (restore.expandedRail && !isSidebarIconCollapsed()) {
    firstVisibleOf(HOST.sidebarCollapse)?.click();
  }
  if (restore.openedDrawer && sidebarShell()) {
    firstVisibleOf(HOST.sidebarClose)?.click();
  }
}

/**
 * Anchor for a composite sidebar card (e.g. the Nexa AI workspace card).
 *
 * Matched by the label on a descendant `<span>` when the sidebar is expanded,
 * falling back to `iconSelector` when it is COLLAPSED — collapsed, these cards
 * drop their labels entirely and render icon-only, which used to make the step
 * vanish from the tour. Pass the icon selector for any card that must survive
 * both states.
 */
export function sidebarCardAnchor(
  label: string | string[],
  node?: string,
  iconSelector?: string,
): AnchorSpec {
  return {
    node,
    structural: {
      within: HOST.sidebar,
      match: HOST.sidebarCard,
      labels: Array.isArray(label) ? label : [label],
      labelWithin: 'span',
      containsSelector: iconSelector,
    },
  };
}

/** Anchor for a submodule row, scoped to its own module's accordion panel. */
export function submenuAnchor(
  moduleLabel: string | string[],
  subLabel: string | string[],
  node?: string,
): AnchorSpec {
  return {
    node,
    submenu: {
      moduleLabels: Array.isArray(moduleLabel) ? moduleLabel : [moduleLabel],
      labels: Array.isArray(subLabel) ? subLabel : [subLabel],
    },
  };
}
